import React from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { TrendingUp, TrendingDown } from '@mui/icons-material';

/**
 * SplitStatsCard Component
 * 
 * A card component split into two halves (top and bottom) for displaying related statistics
 * Perfect for showing Income and Expense in the same card
 * 
 * @param {Object} props.top Top half data { title, value, description, icon, color }
 * @param {Object} props.bottom Bottom half data { title, value, description, icon, color }
 * @param {string} props.tooltip Optional tooltip text
 */
const SplitStatsCard = ({ top, bottom, tooltip = null }) => {
  // Define color mappings
  const colorMappings = {
    indigo: {
      bgGradient: 'from-indigo-50 to-indigo-100/90',
      textColor: 'text-indigo-600',
      borderColor: 'border-indigo-200'
    },
    green: {
      bgGradient: 'from-green-50 to-green-100/90',
      textColor: 'text-green-600',
      borderColor: 'border-green-200'
    },
    red: {
      bgGradient: 'from-red-50 to-red-100/90',
      textColor: 'text-red-600',
      borderColor: 'border-red-200'
    },
    orange: {
      bgGradient: 'from-orange-50 to-orange-100/90',
      textColor: 'text-orange-600',
      borderColor: 'border-orange-200'
    },
    blue: {
      bgGradient: 'from-blue-50 to-blue-100/90',
      textColor: 'text-blue-600',
      borderColor: 'border-blue-200'
    },
    emerald: {
      bgGradient: 'from-emerald-50 to-emerald-100/90',
      textColor: 'text-emerald-600',
      borderColor: 'border-emerald-200'
    },
    purple: {
      bgGradient: 'from-purple-50 to-purple-100/90',
      textColor: 'text-purple-600',
      borderColor: 'border-purple-200'
    }
  };

  // Get colors for top and bottom sections
  const topColors = colorMappings[top.color] || colorMappings.green;
  const bottomColors = colorMappings[bottom.color] || colorMappings.red;

  const renderSection = (data, colors, isTop = true) => {
    const Icon = data.icon;
    const { bgGradient, textColor } = colors;

    return (
      <div className={`flex-1 flex flex-col ${isTop ? 'pb-2' : 'pt-2'}`}>
        <div className="flex items-center mb-1 flex-shrink-0">
          <div className={`bg-gradient-to-br ${bgGradient} p-1 sm:p-1.5 rounded-lg mr-2 shadow-sm flex-shrink-0`}>
            {Icon ? <Icon className={`h-3 w-3 sm:h-4 sm:w-4 ${textColor}`} /> : null}
          </div>
          <h3 className="text-xs sm:text-sm font-medium text-gray-600 truncate">
            {data.title}
          </h3>
        </div>
        
        <div className="flex-1 flex flex-col justify-center pl-8">
          <div className={`text-sm sm:text-base lg:text-lg xl:text-xl font-bold ${textColor} drop-shadow-sm break-words leading-tight`}>
            {data.value}
          </div>
          
          {data.description && (
            <div className="text-xs text-gray-500 mt-0.5 break-words leading-tight">
              {data.description}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="relative bg-gradient-to-br from-white via-white to-gray-50/95 shadow-[0_8px_25px_-8px_rgba(0,0,0,0.3)] hover:shadow-[0_12px_35px_-8px_rgba(0,0,0,0.4)] rounded-xl p-2 sm:p-3 lg:p-4 border border-white/50 backdrop-blur-xl transition-all duration-300 hover:translate-y-[-2px] min-h-[180px] sm:min-h-[200px] flex flex-col">
      {/* Header with title and tooltip */}
      <div className="flex items-center justify-between mb-2 flex-shrink-0">
        <h2 className="text-sm sm:text-base font-semibold text-gray-700">
          Last Month's Overview
        </h2>
        {tooltip && (
          <div className="relative group flex-shrink-0">
            <InformationCircleIcon className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help" />
            <div className="absolute bottom-full right-0 mb-2 w-64 bg-gray-800 text-white text-xs rounded-lg p-3 shadow-lg invisible group-hover:visible transition-all pointer-events-none z-10">
              <p className="font-normal leading-relaxed">
                {tooltip}
              </p>
              <div className="absolute top-full right-4 border-8 border-transparent border-t-gray-800"></div>
            </div>
          </div>
        )}
      </div>

      {/* Top Section (Income) */}
      {renderSection(top, topColors, true)}
      
      {/* Divider */}
      <div className="border-t border-gray-200 my-1"></div>
      
      {/* Bottom Section (Expense) */}
      {renderSection(bottom, bottomColors, false)}
    </div>
  );
};

export default SplitStatsCard;

